<!--
 * @Author: bowang
 * @Date: 2023-07-04 16:04:05
 * @Last Modified by: zhaoyang
 * @Last Modified time: 2025-08-06 12:12:06
-->

<template>
    <div class="blog-page ec-container">
        <div class="blog-content">
            <template v-if="blogpage.blog">
                <img
                    class="back-bt"
                    src="./img/back.svg"
                    @click="back"
                >
                <div class="content-box">
                    <h3 class="blog-title">
                        {{ blogpage.blog.title }}
                    </h3>
                    <div class="blog-desc">
                        <p class="blog-author">
                            {{ blogpage.blog.creatorNickname }}
                        </p>
                        <div class="blog-date">
                            {{ formatTime(blogpage.blog.timeCreated) }}
                        </div>
                    </div>
                    <img
                        class="content-banner-img"
                        :src="blogpage.blog.headerImageUrl"
                        :alt="blogpage.blog.title"
                    >
                    <yqg-md-preview :md-text="blogpage.blog.content" />
                </div>
            </template>
        </div>
    </div>
</template>
<script>
import dayjs from 'dayjs';

import prefetch from 'easycash/common/mixin/server-prefetch';
import Config from 'easycash/common/resource/blog';
import 'dayjs/locale/id';

export default {
    name: 'Blogpage',

    filters: {
        time(time) {
            return dayjs(time).format('DD MMMM YYYY');
        }
    },

    mixins: [prefetch('blogpage')],

    methods: {
        getHead(blogData = {}) {
            const routePath = this.$route.path;
            const {title, headerImageUrl, timeCreated, timeUpdated} = blogData;

            const inner = JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'NewsArticle',
                'headline': title,
                'image': [headerImageUrl],
                'datePublished': new Date(timeCreated).toISOString(),
                'dateModified': new Date(timeUpdated).toISOString(),
                'author': [
                    {
                        '@type': 'Organization',
                        'name': 'Easycash',
                        'url': 'https://easycash.id'
                    }
                ],
                'publisher': {
                    '@type': 'Organization',
                    'name': 'Easycash',
                    'url': 'https://easycash.id',
                    'logo': {
                        '@type': 'ImageObject',
                        'url': 'https://indoi18n-web-cdn.easycash.id/upload/admin/15776836-7b45-4ec4-ba60-2fa3ff69c7ad.png'
                    }
                },
                'description': title,
                'mainEntityOfPage': {
                    '@type': 'WebPage',
                    '@id': 'https://easycash.id' + routePath
                },
                'articleSection': title,
                'keywords': ['Funtopia', 'Easycash', 'PT Indonesia Fintopia', 'team building', 'corporate event'],
                'inLanguage': 'id-ID'
            });

            return '<' + 'script ' + `type="application/ld+json" data-keep="${routePath}">` + inner + '<' + '/' + 'script>';
        },

        async initFetch() {
            const {blogPath} = this.$route.params;
            const {data: {body: blog}} = await Config.getBlog({params: {path: blogPath}});
            if (this?.$ssrContext?.head) {
                try {
                    this.$ssrContext.head = this.$ssrContext.head + this.getHead(blog);
                } catch {
                    // ignore
                }
            }

            return {
                blog
            };
        },

        formatTime(time) {
            return dayjs(time).locale(this.$i18n.locale).format('DD MMMM YYYY');
        },

        back() {
            this.$router.back();
        }
    }
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
div.blog-page {
    padding-top: 1rem;
    margin-top: 1rem;
    display: flex;
    flex-direction: column;

    .back-bt {
        position: absolute;
        left: 0;
        width: 0.32rem;
        height: 0.32rem;
        cursor: pointer;
        background: #000;
        border-radius: 50%;
        padding: 0.1rem;

        &:hover {
            background: #333;
        }
    }

    .blog-content {
        position: relative;
        display: flex;
        justify-content: center;

        .content-box {
            width: 7.5rem;

            .blog-title {
                margin: 0;
                color: #000;
                font-size: 0.32rem;
                font-weight: 700;
                line-height: 1.2;
                font-family: Mozaic HUM;
            }

            .blog-desc {
                display: flex;
                align-items: center;
                font-size: 0.14rem;
                font-family: Mozaic HUM;
                line-height: 0.1rem;
            }

            .blog-author {
                color: #1a5632;
                border-right: 0.01rem solid #d9d9d9;
                padding-right: 0.05rem;
                text-transform: uppercase;
                font-weight: 700;
            }

            .blog-date {
                color: #000;
                padding-left: 0.05rem;
                font-weight: 300;
            }

            .content-banner-img {
                margin-top: 0.1rem;
                width: 100%;
                border-radius: 0.08rem;
            }

            ::v-deep {
                .yqg-md-preview {
                    margin-top: 0.2rem;
                    font-size: 0.14rem;

                    h1,
                    h2 {
                        text-align: start;
                    }

                    ul li {
                        list-style-type: disc;
                    }

                    ol li {
                        list-style-type: dot;
                    }

                    img {
                        margin: 0 auto;
                    }

                    pre {
                        overflow: auto;
                    }

                    a {
                        word-wrap: break-word;
                    }

                    p {
                        font-family: Mozaic HUM;
                    }

                    iframe {
                        width: 100%;
                        height: 100%;
                        aspect-ratio: 16 / 9;
                        border-radius: 0.08rem;
                    }
                }
            }
        }
    }
}

@media screen and (max-width: $screen-md) {
    div.blog-page {
        margin-top: 0.9rem;
        padding-top: 0.9rem;

        .blog-content {
            .back-bt {
                display: none;
            }

            .content-box {
                width: 100%;

                .blog-title {
                    font-size: 0.16rem;
                    line-height: 1.4;
                }

                .blog-desc {
                    transform-origin: 0 0 0;
                    transform: scale(0.85);
                }

                ::v-deep .yqg-md-preview {
                    margin-top: 0.1rem;

                    h3 {
                        font-size: 0.14rem;
                    }
                }
            }
        }
    }
}
</style>
